import { Icon, usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { ExternalLink, Table as BaseTable } from '@/components_deprecated'
import { subWeeks } from 'date-fns'
import type {
  VehicleDocumentsQuery,
  VehicleDocumentsQueryVariables,
  VehicleDocumentNode,
  VehicleExpirationsQuery,
  VehicleExpirationsQueryVariables,
  VehicleExpirationNode,
} from '@/modules/vehicles'
import {
  VEHICLE_DOCUMENTS_QUERY,
  VEHICLE_EXPIRATIONS_QUERY,
  EXPIRATION_TYPES,
} from '@/modules/vehicles'
import { EditVehicleDocumentModal } from '@/modules/vehicles/fleet'
import type {
  EditVehicleDocumentForm,
  EditVehicleDocumentPromptResolve,
  EditVehicleExpirationForm,
  EditVehicleExpirationPromptResolve,
} from '@/modules/vehicles/types'
import { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import styled from 'styled-components'
import { format, formatDistanceStrict } from '@/utils/date-fns'
import { getFileIconAndColor } from '@/utils/file'
import { useConfirm, useOnErrorAuto, usePermissions } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import {
  useVehicleDocumentMutations,
  useVehicleExpirationMutations,
} from '../mutations.hooks'
import { EditVehicleExpirationModal } from './EditVehicleExpirationModal'
import { getVehicleExpirationTypeName } from '../../util'

const Table = styled(BaseTable)`
  position: relative;

  thead {
    text-align: left;
  }

  tbody tr {
    &:hover {
      background: ${props => props.theme.colors.gray10};
    }
  }
`

const TableWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`

const TableRow = styled.tr<{ expired?: boolean }>`
  background: ${props => (props.expired ? props.theme.colors.red12 : 'none')};
`

interface AllVehicleExpirationDatesProps {
  vehicleId: string | null
  disableAdd?: boolean
}

export const AllVehicleExpirationDates: React.FC<
  AllVehicleExpirationDatesProps
> = ({ vehicleId }) => {
  const translations = useTranslate({
    vehicle: 'common.vehicle',
    name: 'common.name',
    add: 'documents.add-document',
    description: 'common.description',
    expirationDate: 'common.expires-at',
    notificationTime: 'common.notification',
    noFiles: 'documents.no-files',
    documentPrompt: {
      delete: 'documents.prompts.delete-document',
      deleteConfirmation: [
        'documents.prompts.delete-document-confirmation',
        { name: '' },
      ],
    },
    expirationDates: 'vehicles.expiration-dates',
    type: 'common.type',
    date: 'common.date',
    noExpirationDates: 'vehicles.no-expiration-dates',
    expirationPrompt: {
      delete: 'vehicles.prompts.delete-expiration-date',
      deleteConfirmation: [
        'documents.prompts.delete-document-confirmation',
        { name: '' },
      ],
    },
  })

  const addPrompt = usePrompt()
  const confirm = useConfirm()
  const onErrorAuto = useOnErrorAuto()

  const { hasPermissions } = usePermissions()
  const canEdit = hasPermissions(
    PERMISSIONS.vehicles.change.vehicledocument,
    true
  )
  const canDelete = hasPermissions(
    PERMISSIONS.vehicles.delete.vehicledocument,
    true
  )

  const { data, loading } = useQuery<
    VehicleDocumentsQuery,
    VehicleDocumentsQueryVariables
  >(VEHICLE_DOCUMENTS_QUERY, {
    variables: {
      vehicle: vehicleId,
      orderBy: 'expirationDate',
    },
    onError: onErrorAuto(),
  })

  const documents = useMemo(() => {
    return data?.allVehicleDocuments?.edges?.map(edge => edge?.node) || []
  }, [data])

  const { patchDocument, deleteDocument } = useVehicleDocumentMutations()

  const { patchExpiration, deleteExpiration } = useVehicleExpirationMutations()

  const { data: expirationData, loading: expirationLoading } = useQuery<
    VehicleExpirationsQuery,
    VehicleExpirationsQueryVariables
  >(VEHICLE_EXPIRATIONS_QUERY, {
    variables: {
      vehicle: vehicleId,
      orderBy: 'expirationDate',
    },
    onError: onErrorAuto(),
  })

  const expirations = useMemo(() => {
    return (
      expirationData?.allVehicleExpirations?.edges?.map(edge => edge?.node) ||
      []
    )
  }, [expirationData?.allVehicleExpirations?.edges])

  function getExpirationAndNotificationTime(
    form: EditVehicleDocumentForm | EditVehicleExpirationForm,
    hasNotification: boolean
  ) {
    const expirationDate = !hasNotification ? null : form.expirationDate
    const notificationTime = !hasNotification
      ? null
      : !expirationDate || !form.notificationTime
      ? null
      : subWeeks(expirationDate, form.notificationTime)
    return { expirationDate, notificationTime }
  }

  async function handleEditDocument(document: VehicleDocumentNode) {
    const { data } = await addPrompt<EditVehicleDocumentPromptResolve | null>(
      resolve => (
        <EditVehicleDocumentModal
          vehicleDocument={document}
          onSubmit={resolve}
        />
      )
    )
    if (!data) return
    const { form, hasNotification } = data
    const { expirationDate, notificationTime } =
      getExpirationAndNotificationTime(form, hasNotification)

    await patchDocument({
      variables: {
        id: document.id,
        input: {
          name: form.name,
          description: form.description,
          expirationDate,
          notificationTime,
          responsibleUser: form.responsibleUser,
        },
      },
    })
  }

  async function handleDeleteDocument(document: VehicleDocumentNode) {
    const { data: answer } = await confirm(
      translations.documentPrompt.deleteConfirmation({ name: document.name }),
      translations.documentPrompt.delete
    )
    if (!answer) return

    void deleteDocument({
      variables: {
        id: document.id,
      },
    })
  }

  async function handleEditExpiration(expiration: VehicleExpirationNode) {
    const { data: promptData } =
      await addPrompt<EditVehicleExpirationPromptResolve | null>(resolve => (
        <EditVehicleExpirationModal
          vehicleExpiration={expiration}
          onSubmit={resolve}
        />
      ))
    if (!promptData) return
    const { form, hasNotification } = promptData
    const { expirationDate, notificationTime } =
      getExpirationAndNotificationTime(form, hasNotification)

    if (!expirationDate) return

    await patchExpiration({
      variables: {
        id: expiration.id,
        input: {
          description: form.description,
          expirationType: form.expirationType,
          expirationDate,
          notificationTime,
          responsibleUser: form.responsibleUser,
          expirationDuration: form.duration,
        },
      },
    })
  }

  async function handleDeleteExpiration(expiration: VehicleExpirationNode) {
    const expirationDate = format(new Date(expiration.expirationDate), 'PP')
    const expirationType = getVehicleExpirationTypeName({ expiration })
    const confirmName = `${expirationDate} - ${expirationType}`
    const { data: answer } = await confirm(
      translations.expirationPrompt.deleteConfirmation({ name: confirmName }),
      translations.expirationPrompt.delete
    )
    if (!answer) return

    void deleteExpiration({
      variables: {
        id: expiration.id,
      },
    })
  }

  return (
    <TableWrapper>
      <Table
        loading={loading}
        noShadow
        noData={!documents.length}
        noDataText={translations.noFiles}
      >
        <thead>
          <tr>
            <th>{translations.vehicle}</th>
            <th>{translations.date}</th>
            <th>{translations.name}</th>
            <th>{translations.notificationTime}</th>
            {canEdit && <th></th>}
            {canDelete && <th></th>}
          </tr>
        </thead>
        <tbody>
          {documents.map(document => {
            const expirationDate = document.expirationDate
              ? new Date(document.expirationDate)
              : null
            const isExpired = !!expirationDate && expirationDate < new Date()
            const expirationDateString = expirationDate
              ? format(expirationDate, 'PP')
              : '-'
            const notificationTime =
              !!document.notificationTime && !!expirationDate
                ? formatDistanceStrict(
                    expirationDate,
                    new Date(document.notificationTime)
                  )
                : '-'
            const [icon, color] = getFileIconAndColor(document.originalName)
            return (
              <TableRow key={document.id} expired={isExpired}>
                <td>{document.vehicle?.registrationNumber}</td>
                <td>{expirationDateString}</td>
                <td>
                  <ExternalLink href={document.file}>
                    <Icon
                      icon={icon}
                      color={color}
                      fixedWidth
                      margin="0 4px 0 0"
                    />
                    {document.name}
                  </ExternalLink>
                </td>
                <td>{notificationTime}</td>
                {canEdit && (
                  <td width="1px" className="center">
                    <Icon
                      icon="edit"
                      cursor="pointer"
                      color="gray6"
                      hoverColor="secondary"
                      onClick={() => handleEditDocument(document)}
                    />
                  </td>
                )}
                {canDelete && (
                  <td>
                    <Icon
                      icon="times"
                      cursor="pointer"
                      color="gray6"
                      hoverColor="red"
                      onClick={() => handleDeleteDocument(document)}
                    />
                  </td>
                )}
              </TableRow>
            )
          })}
        </tbody>
      </Table>
      <Table
        loading={expirationLoading}
        noShadow
        noData={!expirations.length}
        noDataText={translations.noExpirationDates}
      >
        <thead>
          <tr>
            <th>{translations.vehicle}</th>
            <th>{translations.date}</th>
            <th>{translations.type}</th>
            <th>{translations.notificationTime}</th>
            {canEdit && <th></th>}
            {canDelete && <th></th>}
          </tr>
        </thead>
        <tbody>
          {expirations.map(expiration => {
            const expirationType = getVehicleExpirationTypeName({ expiration })
            const expirationDate = expiration.expirationDate
              ? new Date(expiration.expirationDate)
              : null
            const expired = !!expirationDate && expirationDate < new Date()
            const expirationDateString = expirationDate
              ? format(expirationDate, 'PP')
              : '-'
            const notificationTime =
              !!expiration.notificationTime && !!expiration.expirationDate
                ? formatDistanceStrict(
                    new Date(expiration.expirationDate),
                    new Date(expiration.notificationTime)
                  )
                : '-'
            const canEditThisExpiration = canEdit
            const canDeleteThisExpiration =
              canDelete && expirationType !== EXPIRATION_TYPES.nextEuControl
            return (
              <TableRow key={expiration.id} expired={expired}>
                <td>{expiration.vehicle?.registrationNumber}</td>
                <td>{expirationDateString}</td>
                <td className="wrap">{expirationType}</td>
                <td>{notificationTime}</td>
                {canEdit && (
                  <td width="1px" className="center">
                    {canEditThisExpiration && (
                      <Icon
                        icon="edit"
                        cursor="pointer"
                        color="gray6"
                        hoverColor="secondary"
                        onClick={() => handleEditExpiration(expiration)}
                      />
                    )}
                  </td>
                )}
                {canDelete && (
                  <td>
                    {canDeleteThisExpiration && (
                      <Icon
                        icon="times"
                        cursor="pointer"
                        color="gray6"
                        hoverColor="red"
                        onClick={() => handleDeleteExpiration(expiration)}
                      />
                    )}
                  </td>
                )}
              </TableRow>
            )
          })}
        </tbody>
      </Table>
    </TableWrapper>
  )
}
